import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shell = ({ children, testId }) => (
  <div className="flex min-h-[70vh] items-center justify-center" data-testid={testId}>
    <div className="w-full max-w-md rounded-3xl border border-[#E9EDF7] bg-white p-10 text-center shadow-[0px_18px_40px_rgba(112,144,176,0.14)]">{children}</div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking');
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return undefined; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/lead-store/status/${sessionId}`);
        setOrder(res.data);
        if (res.data.payment_status === 'paid') { setState('paid'); return; }
        if (['expired', 'failed'].includes(res.data.payment_status)) { setState('error'); return; }
      } catch { /* retry */ }
      if (attempts.current >= 8) { setState('timeout'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell testId="payment-success-page">
      {state === 'checking' && (<>
        <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#2447D6]" />
        <h1 className="mt-5 font-['Manrope'] text-2xl font-extrabold text-[#2B3674]">Confirming your payment…</h1>
        <p className="mt-2 text-sm text-[#707EAE]">Hang tight — this only takes a moment.</p>
      </>)}
      {state === 'paid' && (<>
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
        <h1 className="mt-5 font-['Manrope'] text-2xl font-extrabold text-[#2B3674]" data-testid="payment-success-title">Your Breeze IUL Leads order has been received!</h1>
        <p className="mt-2 text-sm text-[#707EAE]">Your order is scheduled to begin fulfillment <b>Monday afternoon</b>{order?.fulfillment_starts ? ` (${new Date(`${order.fulfillment_starts}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` : ''}. A confirmation has been emailed to you.</p>
        {order?.order_number && <p className="mt-3 inline-block rounded-full bg-[#F4F7FE] px-3 py-1 font-mono text-xs font-bold text-[#2447D6]" data-testid="payment-order-number">Order #{order.order_number}</p>}
      </>)}
      {state === 'timeout' && (<>
        <Clock className="mx-auto h-14 w-14 text-[#FF9000]" />
        <h1 className="mt-5 font-['Manrope'] text-2xl font-extrabold text-[#2B3674]">Still processing</h1>
        <p className="mt-2 text-sm text-[#707EAE]">Your payment is being finalized. Check your receipts in a minute — you won't be charged twice.</p>
      </>)}
      {state === 'error' && (<>
        <XCircle className="mx-auto h-14 w-14 text-red-500" />
        <h1 className="mt-5 font-['Manrope'] text-2xl font-extrabold text-[#2B3674]">Payment not completed</h1>
        <p className="mt-2 text-sm text-[#707EAE]">We couldn't confirm this payment. If you were charged, contact the Breeze team.</p>
      </>)}
      <button type="button" onClick={() => navigate('/leads')} className="mt-8 rounded-full bg-[#2447D6] px-6 py-3 text-sm font-extrabold text-white" data-testid="payment-back-btn">Back to Lead Store</button>
    </Shell>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <Shell testId="payment-cancel-page">
      <XCircle className="mx-auto h-14 w-14 text-[#A3AED0]" />
      <h1 className="mt-5 font-['Manrope'] text-2xl font-extrabold text-[#2B3674]">Checkout cancelled</h1>
      <p className="mt-2 text-sm text-[#707EAE]">No charge was made. You can pick a lead pack whenever you're ready.</p>
      <button type="button" onClick={() => navigate('/leads')} className="mt-8 rounded-full bg-[#2447D6] px-6 py-3 text-sm font-extrabold text-white" data-testid="payment-back-btn">Back to Lead Store</button>
    </Shell>
  );
}
